import React, {
  useEffect,
  useState,
  useCallback,
  useRef,
  useMemo,
} from 'react';
import P from 'prop-types';
import _ from 'lodash';
import './Section.styl';
import Page from './Page';
import { getNodes } from './utils';

interface Props {
  pageSize: string;
  pageHeader: any;
  pageFooter: any;
  children: any;
  pagePadding: string;
  reportPagePadding: string;
  reportPagesCount: number;
  updatePagesCount: Function;
  sectionStartPageNumber: number;
}

function Section({
  pageHeader = [],
  pageFooter = [],
  pageSize = 'A4',
  children,
  pagePadding,
  reportPagePadding,
  reportPagesCount = 0,
  updatePagesCount,
  sectionStartPageNumber,
}: Props) {
  const [pages, setPages] = useState<any>([]);
  const [version, setVersion] = useState(0);
  const nodes = useRef<any[]>([]);

  const isCalculatingRenderNodes = useRef(false);

  const getNextNode = () => {
    let node: any = _.first(nodes.current);
    if (node) {
      if (node.type === 'TABLE') {
        if (!node.children.length) {
          node = node.bottom || <div />;
        } else if (!node.isHeaderSettled) {
          node = [node.header, _.first(node.children)];
        } else {
          node = _.first(node.children);
        }
      }
    }
    return node;
  };

  const getUndoNode = (pagesState: any[] = []) => {
    // @ts-ignore
    let lastPageChildren = _.last(pagesState).children;
    let node: any = _.last(lastPageChildren);
    if (
      node.props &&
      (node.props.isRepeatedHeader || node.props.isRepeatedSectionItem)
    ) {
      if (node.props && node.props.isRepeatedSectionItem) {
        // @ts-ignore
        _.first(nodes.current).children.unshift(node);
      }
      // @ts-ignore
      node = [_.first(nodes.current).header, node];
    } else {
      nodes.current.unshift(node);
    }
    // @ts-ignore
    _.last(pagesState).children = _.slice(
      lastPageChildren,
      0,
      lastPageChildren.length - 1,
    );

    // Prevent table header to be last node on page
    // @ts-ignore
    lastPageChildren = _.last(pagesState).children;
    const pageLastNode: any = _.last(lastPageChildren);
    if (
      pageLastNode &&
      pageLastNode.props &&
      pageLastNode.props.isRepeatedHeader
    ) {
      // @ts-ignore
      _.last(pagesState).children = _.slice(
        lastPageChildren,
        0,
        lastPageChildren.length - 1,
      );
    }

    return node;
  };

  const shiftNode = () => {
    const node = _.first(nodes.current);
    if (node) {
      if (node.type === 'TABLE' && node.children.length) {
        if (!node.isHeaderSettled) {
          node.isHeaderSettled = true;
        }
        node.children = node.children.slice(1);
      } else {
        nodes.current = nodes.current.slice(1);
      }
    }
  };

  const renderNodes = useCallback(
    ({ undo = false, createNewPage = false }) => {
      setPages((pagesState: any[]) => {
        let node = getNextNode();
        if (!node && !undo) {
          // Done
          return pagesState;
        }
        if (isCalculatingRenderNodes.current) return pagesState;
        isCalculatingRenderNodes.current = true;
        if (undo) {
          node = getUndoNode(pagesState);
        }
        if (!node) {
          isCalculatingRenderNodes.current = false;
          return pagesState;
        }
        if (!pagesState.length || createNewPage) {
          pagesState.push({
            children: [],
          });
        }
        _.forEach(_.flatten([node]), item => {
          _.last(pagesState).children.push(item);
        });
        _.last(pagesState).forceSettle = false;
        if (undo && createNewPage) {
          _.last(pagesState).forceSettle = true;
        }
        shiftNode();
        isCalculatingRenderNodes.current = false;
        setTimeout(() => {
          setVersion(state => state + 1);
          updatePagesCount(pagesState.length);
        }, 0);
        return pagesState;
      });
      // eslint-disable-next-line
    },
    // eslint-disable-next-line
    [nodes],
  );

  useEffect(() => {
    setPages([]);
    const sectionProps = {};
    nodes.current = getNodes(children, sectionProps);
    renderNodes({});
    // eslint-disable-next-line
  }, [children]);

  const renderedPages = useMemo(
    () =>
      _.map(pages, (page, index) => (
        <Page
          key={index}
          pageSize={pageSize}
          childrenNodes={page.children}
          // @ts-ignore
          settled={index !== pages.length - 1}
          onSettled={renderNodes}
          forceSettle={page.forceSettle}
          pageHeader={pageHeader}
          pageFooter={pageFooter}
          pageNumber={index + 1}
          sectionPagesCount={pages.length}
          reportPagesCount={reportPagesCount}
          sectionStartPageNumber={sectionStartPageNumber}
          padding={pagePadding || reportPagePadding}
        />
        )),
    // eslint-disable-next-line
    [pages, version, reportPagesCount],
  );

  return <div className="mio-section">{renderedPages}</div>;
}

Section.propTypes = {
  pageSize: P.string,
  pageHeader: P.any,
  pageFooter: P.any,
  children: P.any.isRequired,
  pagePadding: P.string,
  reportPagePadding: P.string,
  reportPagesCount: P.number,
  updatePagesCount: P.func,
  sectionStartPageNumber: P.number,
};

Section.defaultProps = {
  pageSize: 'A4',
  pageHeader: [],
  pageFooter: [],
  pagePadding: '',
  reportPagePadding: '20.8 14 19 14',
  reportPagesCount: 0,
  updatePagesCount: () => {},
  sectionStartPageNumber: 0,
};

export default Section;
